import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type PortalLanguage = 'vi' | 'en';

/**
 * EAA-1845: thin wrapper over i18next's own language state — no separate
 * React context needed (unlike `useTheme`/`ThemeProvider`) because i18next is
 * already the shared singleton, and `useTranslation()` already re-renders
 * this hook's callers on every `languageChanged` event. Persistence and the
 * `<html lang>` sync live once in `src/i18n/index.ts`, not here, so they run
 * exactly once regardless of how many components call this hook.
 */
export function useLanguage() {
    const { i18n } = useTranslation();
    const language: PortalLanguage = i18n.language === 'en' ? 'en' : 'vi';

    const setLanguage = useCallback(
        (next: PortalLanguage) => {
            void i18n.changeLanguage(next);
        },
        [i18n]
    );

    const toggleLanguage = useCallback(() => {
        void i18n.changeLanguage(language === 'vi' ? 'en' : 'vi');
    }, [i18n, language]);

    return { language, setLanguage, toggleLanguage };
}
